import {
  Home,
  Globe,
  FileText,
  Users,
  CheckSquare,
  Calendar,
  BarChart3,
  User,
  Sparkles,
  Play,
  RotateCcw,
  CheckCircle2,
} from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import type { TourId, TourDefinition } from '../../types/onboarding';

const ICON_MAP: Record<string, React.ElementType> = {
  Home,
  Globe,
  FileText,
  Users,
  CheckSquare,
  Calendar,
  BarChart3,
  User,
  Sparkles,
};

interface TourListProps {
  onStartTour?: () => void;
}

export function TourList({ onStartTour }: TourListProps) {
  const {
    tours,
    isLoading,
    isTourDone,
    getTourProgress,
    startTour,
    startFullTour,
    resetTour,
    resetAllTours,
    isActive,
  } = useOnboarding();

  const handleStartTour = async (tourId: TourId) => {
    await startTour(tourId);
    onStartTour?.();
  };

  const handleStartFullTour = async () => {
    await startFullTour();
    onStartTour?.();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Group tours by category
  const groupedTours = tours.reduce((acc, tour) => {
    if (!acc[tour.category]) {
      acc[tour.category] = [];
    }
    acc[tour.category].push(tour);
    return acc;
  }, {} as Record<string, TourDefinition[]>);

  const categoryLabels: Record<string, string> = {
    core: 'Grundlagen',
    marketing: 'Marketing',
    productivity: 'Produktivität',
    settings: 'Einstellungen',
  };

  const categoryOrder = ['core', 'marketing', 'productivity', 'settings'];

  return (
    <div className="space-y-6">
      {/* Full Tour Button */}
      <div className="bg-gradient-primary-soft rounded-2xl p-5 border border-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
              Komplette Einführung
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-300 mb-3">
              Lerne alle wichtigen Funktionen von MAMAL in einer geführten Tour kennen.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleStartFullTour}
                disabled={isActive}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                <Play className="w-4 h-4" />
                Tour starten
              </button>
              <button
                onClick={resetAllTours}
                disabled={isActive}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-slate-300 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50 rounded-xl transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                Alle zurücksetzen
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Tours by Category */}
      {categoryOrder.map((category) => {
        const categoryTours = groupedTours[category];
        if (!categoryTours?.length) return null;

        return (
          <div key={category}>
            <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-3 px-1">
              {categoryLabels[category]}
            </h4>
            <div className="space-y-2">
              {categoryTours.map((tour) => (
                <TourCard
                  key={tour.id}
                  tour={tour}
                  isCompleted={isTourDone(tour.id as TourId)}
                  progress={getTourProgress(tour.id as TourId)}
                  onStart={() => handleStartTour(tour.id as TourId)}
                  onReset={() => resetTour(tour.id as TourId)}
                  disabled={isActive}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface TourCardProps {
  tour: TourDefinition;
  isCompleted: boolean;
  progress: number;
  onStart: () => void;
  onReset: () => void;
  disabled: boolean;
}

function TourCard({
  tour,
  isCompleted,
  progress,
  onStart,
  onReset,
  disabled,
}: TourCardProps) {
  const Icon = ICON_MAP[tour.icon] || Sparkles;

  return (
    <div className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl hover:border-gray-200 dark:hover:border-slate-600 transition-colors">
      {/* Icon */}
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCompleted
            ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
            : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
        }`}
      >
        {isCompleted ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : (
          <Icon className="w-5 h-5" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900 dark:text-white">{tour.name}</h4>
          {isCompleted && (
            <span className="px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-full">
              Abgeschlossen
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{tour.description}</p>

        {/* Progress bar (if in progress) */}
        {!isCompleted && progress > 0 && (
          <div className="mt-2 h-1.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {isCompleted ? (
          <button
            onClick={onReset}
            disabled={disabled}
            className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            title="Zurücksetzen"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        ) : null}
        <button
          onClick={onStart}
          disabled={disabled}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors disabled:opacity-50"
        >
          <Play className="w-3.5 h-3.5" />
          {isCompleted ? 'Wiederholen' : 'Starten'}
        </button>
      </div>
    </div>
  );
}
